import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, GitBranch, Users, KeyRound } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ApiKeysCard } from "@/components/api-keys-card";

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ name: "", cloud: "azure", language: "terraform", url: "", description: "" });
  const [resetResult, setResetResult] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") {
      client.get("/admin/users").then((r) => setUsers(r.data));
      client.get("/admin/templates").then((r) => setTemplates(r.data));
    }
  }, [user]);

  if (user?.role !== "admin") {
    return <div className="p-10 text-sm text-slate-500">Admin only.</div>;
  }

  const addTemplate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await client.post("/admin/templates", form);
      setTemplates([...templates, data]);
      setForm({ name: "", cloud: "azure", language: "terraform", url: "", description: "" });
      toast.success("Template registered");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const removeTemplate = async (id) => {
    await client.delete(`/admin/templates/${id}`);
    setTemplates(templates.filter((t) => t.id !== id));
    toast.success("Template removed");
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset the password of ${u.email}? Their sessions will be signed out.`)) return;
    try {
      const { data } = await client.post(`/admin/users/${u.id}/reset-password`);
      setResetResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="p-10">
      <div className="mb-8">
        <p className="font-mono-tight text-xs uppercase tracking-widest text-slate-500">// admin</p>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight" data-testid="page-title">Workspace administration</h1>
        <p className="mt-1 text-sm text-slate-600">Manage users and register external IaC template sources.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ApiKeysCard />
        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none">
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-slate-400" />
              <h2 className="font-display text-lg font-semibold">Users</h2>
            </div>
            <Badge className="rounded-full bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600">{users.length}</Badge>
          </div>
          <div className="divide-y divide-slate-100">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between py-3" data-testid={`user-${u.email}`}>
                <div>
                  <div className="text-sm font-medium text-slate-900">{u.name}</div>
                  <div className="font-mono-tight text-xs text-slate-500">{u.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="rounded-full bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600">{u.role}</Badge>
                  {u.id !== user.id && (
                    <Button variant="ghost" size="sm" className="h-8 text-slate-600" onClick={() => resetPassword(u)} data-testid={`reset-password-${u.email}`}>
                      <KeyRound className="mr-1.5 h-3.5 w-3.5" /> Reset password
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
            <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">Temporary password</DialogTitle>
                <DialogDescription>Share it with {resetResult?.email} over a secure channel. It is shown only once; they should change it right after signing in.</DialogDescription>
              </DialogHeader>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono-tight text-sm" data-testid="temporary-password">{resetResult?.temporary_password}</code>
                <Button variant="outline" className="h-10 border-slate-200" onClick={() => { navigator.clipboard?.writeText(resetResult.temporary_password); toast.success("Copied"); }} data-testid="copy-temporary-password">Copy</Button>
              </div>
            </DialogContent>
          </Dialog>
        </Card>

        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-none">
          <div className="mb-6 flex items-center gap-2">
            <GitBranch className="h-4 w-4 text-slate-400" />
            <h2 className="font-display text-lg font-semibold">Git template sources</h2>
          </div>
          <form onSubmit={addTemplate} className="space-y-3" data-testid="template-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Name</Label>
                <Input required data-testid="tpl-name" className="mt-2 h-10 border-slate-200" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Cloud</Label>
                <Select value={form.cloud} onValueChange={(v) => setForm({ ...form, cloud: v })}>
                  <SelectTrigger data-testid="tpl-cloud" className="mt-2 h-10 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="azure">Azure</SelectItem>
                    <SelectItem value="aws">AWS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Language</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger data-testid="tpl-language" className="mt-2 h-10 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="terraform">Terraform</SelectItem>
                    <SelectItem value="bicep">Bicep</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Repository URL</Label>
                <Input required data-testid="tpl-url" placeholder="https://github.com/…" className="mt-2 h-10 border-slate-200 font-mono-tight text-xs" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Description</Label>
              <Textarea rows={2} data-testid="tpl-description" className="mt-2 border-slate-200" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <Button type="submit" data-testid="tpl-submit" className="h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800">
              Register template
            </Button>
          </form>

          <div className="mt-6 divide-y divide-slate-100">
            {templates.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-3" data-testid={`tpl-${t.name}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">{t.name}</div>
                  <div className="truncate font-mono-tight text-xs text-slate-500">{t.url}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="rounded-full bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600">{t.cloud}</Badge>
                  <Badge className="rounded-full bg-slate-100 text-[10px] uppercase tracking-widest text-slate-600">{t.language}</Badge>
                  <Button variant="ghost" size="icon" onClick={() => removeTemplate(t.id)} data-testid={`tpl-delete-${t.name}`}>
                    <Trash2 className="h-4 w-4 text-slate-500" />
                  </Button>
                </div>
              </div>
            ))}
            {!templates.length && (
              <p className="py-3 text-sm text-slate-500">No external templates yet.</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
